import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";

export type TicketType = "bug" | "feature" | "question";

interface OpenTicketOptions {
  type?: TicketType;
  prefillTitle?: string;
}

interface SupportContextValue {
  helpOpen: boolean;
  openHelp: () => void;
  closeHelp: () => void;
  ticketOpen: boolean;
  ticketType: TicketType;
  prefillTitle: string;
  openTicket: (opts?: OpenTicketOptions) => void;
  closeTicket: () => void;
}

const SupportContext = createContext<SupportContextValue | null>(null);

export function SupportProvider({ children }: { children: ReactNode }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketType, setTicketType] = useState<TicketType>("bug");
  const [prefillTitle, setPrefillTitle] = useState("");

  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const openTicket = useCallback((opts?: OpenTicketOptions) => {
    setTicketType(opts?.type ?? "bug");
    setPrefillTitle(opts?.prefillTitle ?? "");
    setHelpOpen(false);
    setTicketOpen(true);
  }, []);
  const closeTicket = useCallback(() => setTicketOpen(false), []);

  const value = useMemo(
    () => ({
      helpOpen,
      openHelp,
      closeHelp,
      ticketOpen,
      ticketType,
      prefillTitle,
      openTicket,
      closeTicket,
    }),
    [helpOpen, openHelp, closeHelp, ticketOpen, ticketType, prefillTitle, openTicket, closeTicket]
  );

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error("useSupport must be used within a SupportProvider");
  return ctx;
}
